import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Tag, Space, Modal, Row, Col } from 'antd';
import { get, put, post, getMerchantApps, getCurrentAppId } from '../../utils/merchantApi';
import CryptoJS from 'crypto-js';

const MerchantSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [appInfo, setAppInfo] = useState(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [secretModalVisible, setSecretModalVisible] = useState(false);
  const [appSecret, setAppSecret] = useState('');
  const [fetchingSecret, setFetchingSecret] = useState(false);

  useEffect(() => {
    fetchAppInfo();
  }, []);

  const fetchAppInfo = async () => {
    setLoading(true);
    try {
      const apps = getMerchantApps();
      const currentAppId = getCurrentAppId();
      
      if (!currentAppId) {
        message.error('请先选择应用');
        return;
      }

      const response = await get('/merchant/info');
      setAppInfo(response.data);
      form.setFieldsValue({
        app_name: response.data.app_name,
        app_desc: response.data.app_desc,
        callback_url: response.data.callback_url,
        webhook_url: response.data.webhook_url,
        ip_whitelist: response.data.ip_whitelist
      });
    } catch (error) {
      console.error('获取应用信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await put('/merchant/info', {
        callback_url: values.callback_url,
        webhook_url: values.webhook_url,
        ip_whitelist: values.ip_whitelist
      });
      message.success('保存成功');
    } catch (error) {
      console.error('保存失败:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      const oldPassword = CryptoJS.SHA256(values.oldPassword).toString();
      const newPassword = CryptoJS.SHA256(values.newPassword).toString();
      
      await post('/merchant/password', {
        oldPassword,
        newPassword
      });
      
      message.success('密码修改成功');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      console.error('修改密码失败:', error);
    }
  };

  const handleViewSecret = async () => {
    setFetchingSecret(true);
    try {
      const response = await get('/merchant/secret');
      setAppSecret(response.data.app_secret);
      setSecretModalVisible(true);
    } catch (error) {
      console.error('获取密钥失败:', error);
    } finally {
      setFetchingSecret(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {appInfo && (
        <Card title="应用信息" style={{ marginBottom: 16 }}>
          <Space size="large" wrap>
            <div>
              <span style={{ color: '#666' }}>应用ID：</span>
              <span style={{ fontFamily: 'monospace' }}>{appInfo.app_id}</span>
            </div>
            <div>
              <span style={{ color: '#666' }}>状态：</span>
              <Tag color={appInfo.status === 1 ? 'green' : 'red'}>
                {appInfo.status === 1 ? '启用' : '禁用'}
              </Tag>
            </div>
            <div>
              <span style={{ color: '#666' }}>日限额：</span>
              <span>¥{Number(appInfo.daily_limit || 0).toLocaleString()}</span>
            </div>
            <div>
              <span style={{ color: '#666' }}>单笔限额：</span>
              <span>¥{appInfo.single_limit_min} - ¥{appInfo.single_limit_max}</span>
            </div>
            <div>
              <Button type="link" onClick={handleViewSecret} loading={fetchingSecret}>
                查看密钥
              </Button>
            </div>
          </Space>
        </Card>
      )}

      <Card title="回调配置">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item name="app_name" label="应用名称">
            <Input disabled />
          </Form.Item>

          <Form.Item name="app_desc" label="应用描述">
            <Input.TextArea rows={2} disabled />
          </Form.Item>

          <Form.Item 
            name="callback_url" 
            label="支付回调地址"
            extra="用户支付完成后跳转的页面"
          >
            <Input placeholder="例如：https://example.com/payment/callback" />
          </Form.Item>

          <Form.Item 
            name="webhook_url" 
            label="Webhook通知地址"
            extra="支付成功后异步通知的地址"
          >
            <Input placeholder="例如：https://example.com/payment/notify" />
          </Form.Item>

          <Form.Item 
            name="ip_whitelist" 
            label="IP白名单"
            extra="多个IP用逗号分隔，留空表示不限制"
          >
            <Input placeholder="例如：192.168.1.1,10.0.0.0/8" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="账户安全" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col>
            <Button type="primary" onClick={() => setPasswordModalVisible(true)}>
              修改登录密码
            </Button>
          </Col>
        </Row>
      </Card>

      <Modal
        title="查看密钥"
        open={secretModalVisible}
        onCancel={() => {
          setSecretModalVisible(false);
          setAppSecret('');
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setSecretModalVisible(false);
            setAppSecret('');
          }}>
            我知道了
          </Button>
        ]}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 10, color: '#ff4d4f', fontWeight: 500 }}>
            请妥善保存 App Secret ！
          </p>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '15px', 
            borderRadius: 4,
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            fontSize: 14
          }}>
            {appSecret}
          </div>
        </div>
      </Modal>

      <Modal
        title="修改登录密码"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="oldPassword"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请确认新密码" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MerchantSettings;
